import React from 'react';
import { Modal } from '../../../components/common/Modal';
import { CreatePurchaseOrderForm } from '../CreatePurchaseOrderForm';
import { SupplierForm } from '../../purchasing/SupplierForm';
import { GreenBeanProfileForm } from '../../purchasing/GreenBeanProfileForm';
import type { PurchaseOrder, PurchaseOrderFormData } from '../../../types/purchaseOrder';
import type { Supplier } from '../../../types/supplier';
import type { ProfileModalData } from '../hooks/usePurchaseOrderListState';
import { useLanguage } from '../../../features/language/useLanguage';

interface PurchaseOrderModalsProps {
  editingPO: PurchaseOrder | null;
  onCloseEdit: () => void;
  onUpdatePO: (data: PurchaseOrderFormData) => Promise<void>;
  viewingSupplier: Supplier | null;
  isSupplierModalOpen: boolean;
  onCloseSupplier: () => void;
  profileModalData: ProfileModalData;
  onCloseProfile: () => void;
  onProfileSuccess: () => void;
  receivingPO: PurchaseOrder | null;
  onCloseReceive: () => void;
  onReceiveSubmit: (data: PurchaseOrderFormData) => Promise<void>;
  payingPO: PurchaseOrder | null;
  onClosePay: () => void;
  onPaySubmit: (data: PurchaseOrderFormData) => Promise<void>;
  readOnly?: boolean;
}

export const PurchaseOrderModals: React.FC<PurchaseOrderModalsProps> = ({
  editingPO,
  onCloseEdit,
  onUpdatePO,
  viewingSupplier,
  isSupplierModalOpen,
  onCloseSupplier,
  profileModalData,
  onCloseProfile,
  onProfileSuccess,
  receivingPO,
  onCloseReceive,
  onReceiveSubmit,
  payingPO,
  onClosePay,
  onPaySubmit,
  readOnly = false,
}) => {
  const { t } = useLanguage();
  return (
    <>
      <Modal
        isOpen={!!editingPO}
        onClose={onCloseEdit}
        title={readOnly ? `${t('common.view')} ${editingPO?.orderNumber}` : `${t('purchaseOrder.actions.edit')} ${editingPO?.orderNumber}`}
        className="max-w-6xl"
      >
        {editingPO && <CreatePurchaseOrderForm initialData={editingPO} onSubmit={onUpdatePO} onCancel={onCloseEdit} readOnly={readOnly} />}
      </Modal>

      <Modal maxWidth="xl" isOpen={isSupplierModalOpen} onClose={onCloseSupplier} title={t('purchasing.sampleList.table.supplier')}>
        <SupplierForm initialData={viewingSupplier || undefined} onCancel={onCloseSupplier} readOnly={true} />
      </Modal>

      <Modal
        isOpen={profileModalData.isOpen}
        onClose={onCloseProfile}
        title={profileModalData.existingProfile ? t('greenBeanProfile.title') : t('greenBeanProfile.actions.new')}
        className="max-w-4xl"
      >
        {profileModalData.isOpen && (
          <GreenBeanProfileForm
            initialData={
              profileModalData.existingProfile
                ? profileModalData.existingProfile
                : profileModalData.templateProfile
                ? {
                    ...profileModalData.templateProfile,
                    id: undefined,
                    created_at: undefined,
                    updated_at: undefined,
                    lot_id: profileModalData.item?.lotId,
                    inventory_item_id: profileModalData.item?.inventoryItemId,
                    is_default_profile: false,
                  }
                : {
                    // No entityType/entityId is passed to the form here, so `type` must come
                    // from initialData — the backend rejects creates without it. Matches the
                    // template branch above, which inherits 'ItemType' from the item profile.
                    type: 'ItemType',
                    lot_id: profileModalData.item?.lotId,
                    inventory_item_id: profileModalData.item?.inventoryItemId,
                    profile_name: profileModalData.item?.name,
                    is_default_profile: false,
                  }
            }
            hideLotSection={true}
            onSuccess={onProfileSuccess}
            onCancel={onCloseProfile}
          />
        )}
      </Modal>

      <Modal
        isOpen={!!receivingPO}
        onClose={onCloseReceive}
        title={`${t('purchaseOrder.form.submit.receive')} - ${receivingPO?.orderNumber}`}
        maxWidth='6xl'
      >
        {receivingPO && (
          <CreatePurchaseOrderForm
            mode="receive"
            initialData={{
              ...receivingPO,
              deliveryStatus: 'received',
              actualDeliveryDate: new Date().toISOString().split('T')[0],
            }}
            onSubmit={onReceiveSubmit}
            onCancel={onCloseReceive}
          />
        )}
      </Modal>

      <Modal
        isOpen={!!payingPO}
        onClose={onClosePay}
        title={`${t('dashboard.outstandingPayments.markAsPaid')} - ${payingPO?.orderNumber}`}
        className="max-w-lg"
      >
        {payingPO && (
          <CreatePurchaseOrderForm
            mode="pay"
            initialData={{
              ...payingPO,
              paymentStatus: 'paid',
            }}
            onSubmit={onPaySubmit}
            onCancel={onClosePay}
          />
        )}
      </Modal>
    </>
  );
};
